import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import ReusableTable from '../../components/other/ReuseableTable'
import { Link, useNavigate } from 'react-router-dom'
import Loader from '../pages/loader'
import { toast } from 'react-toastify'
import { useGetDomainsQuery, useLazyGetDomainsQuery } from '../../redux/apis/domainsManagement/domains/dMDomainsApiSlice'
import { can } from '../../helpers/auth'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import DomainCategoryDropdown from '../../components/DomainCategoryDropdown'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'

const DomainsInfo = React.memo(() => {
  const context = useContext(AppSettings)
  const navigate = useNavigate()

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})

  const priorityMap = {
    1: 'Low',
    2: 'Medium',
    3: 'High'
  }

  const { data, isLoading, error, isFetching } = useGetDomainsQuery({
    expand: ['last_updated_by', 'domain_category'].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    fields: [
      'id',
      'domain_name',
      'is_active',
      'crawling_priority',
      'reporting_abuse_to',
      'last_updated_by',
      'domain_category',
      'is_related',
      'to_crawl'
    ].join(','),
    ...(search ? { search } : {}),
    ...filters
  })

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'DOMAIN NAME',
      dataIndex: 'domain_name',
      key: 'domain_name',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'DOMAIN STATUS',
      dataIndex: 'is_active',
      key: 'is_active',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Active', value: true },
        { label: 'Inactive', value: false }
      ],
      render: text => <p>{text ? 'Active' : 'Inactive'}</p>
    },
    {
      title: 'TO CRAWL',
      dataIndex: 'to_crawl',
      key: 'to_crawl',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { label: 'Yes', value: true },
        { label: 'No', value: false }
      ],
      render: text => <p>{text ? 'YES' : 'NO'}</p>
    },
    {
      title: 'CRAWL PRIORITY',
      dataIndex: 'crawling_priority',
      key: 'crawling_priority',
      filter: true,
      filterType: 'select',
      filterOptions: Object.entries(priorityMap).map(([value, label]) => ({
        label,
        value: Number(value)
      })),
      render: text => <p>{priorityMap[text]}</p>
    },
    {
      title: 'REPORTING ABUSE TO',
      dataIndex: 'reporting_abuse_to',
      key: 'reporting_abuse_to',

    },
    {
      title: 'DOMAIN CATEGORY',
      dataIndex: 'domain_category',
      key: 'domain_category.domain_category',
      filter: true,
      filterType: 'text',
      filterComponent: DomainCategoryDropdown,
      render: text => <p>{text?.domain_category}</p>
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (text, record) => (
        <div className='flex gap-2'>
          {can('put_domains') && (
            <Link
              to={`/domains/domains-info/${record?.id}/info`}
              className='btn btn-outline-theme'
            >
              Edit
            </Link>
          )}
          {can('put_domains') && (
            <Link
              to={`/domains/domains-info/${record?.id}/xpath`}
              className='btn btn-outline-theme'
            >
              XPath
            </Link>
          )}
        </div>
      )
    }
  ]

  useEffect(() => {
    if (error?.data) {
      const errorMessages = Object.values(error.data).flat()
      errorMessages.forEach(msg => toast.error(msg))
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || isFetching} />

      <div className='flex mb-4'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetDomainsQuery}
            fileName='domains'
          />
          {can('add_domain') && (
            <button
              className='btn btn-outline-theme ml-auto mb-4'
              onClick={() => navigate('/domains/domains-info/0/info')}
            >
              Create New Domain
            </button>
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default DomainsInfo
